import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { canWith, type MyPermissions } from '@tradeoutlet/shared';
import { api } from './api';
import { queryClient } from './queryClient';
import { useWorkspaceId } from './hooks';

/**
 * Permission checks in the web app. The server is always the authority; these hooks only decide what to show.
 * - usePermissions(): my workspace-wide permissions plus scoped extras (GET /v1/me/permissions).
 * - usePermissionsAt(scope): effective permissions at one resource, e.g. "location:<id>" or "conversation:<id>"
 *   (the server resolves containment, e.g. a channel placed in a building).
 */
export const permKeys = { all: ['perm'] as const, mine: (ws: string) => ['perm', ws, 'mine'] as const, at: (ws: string, scope: string) => ['perm', ws, 'at', scope] as const };

export function usePermissions() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: permKeys.mine(ws ?? ''), queryFn: () => api.myPermissions(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}

export function usePermissionsAt(scope: string | null | undefined) {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: permKeys.at(ws ?? '', scope ?? ''), queryFn: () => api.myPermissions(ws!, scope), enabled: Boolean(ws && scope), staleTime: 60_000,
    select: (d: MyPermissions) => new Set(d.at?.permissions ?? []),
  });
}

/** Is `perm` held workspace-wide, or (when refs are given) at one of those scope refs? */
export function useCan(perm: string, refs: readonly string[] = []): boolean {
  const mine = usePermissions().data;
  return canWith(mine, perm, refs);
}

/** Held anywhere: workspace-wide or at any scope (for showing a page whose content is then scope-filtered). */
export function useCanAnywhere(perm: string): boolean {
  const mine = usePermissions().data;
  return Boolean(mine && (mine.workspace.includes(perm) || mine.scoped.some(s => s.permissions.includes(perm))));
}

/** Scope refs where `perm` is held ('all' when workspace-wide). */
export function useScopesWith(perm: string): 'all' | string[] {
  const mine = usePermissions().data;
  if (!mine) return [];
  if (mine.workspace.includes(perm)) return 'all';
  return mine.scoped.filter(s => s.permissions.includes(perm)).map(s => s.scope);
}

/** Location ids where `perm` is held, or 'all'. */
export function useLocationsWith(perm: string): 'all' | string[] {
  const s = useScopesWith(perm);
  return s === 'all' ? 'all' : s.filter(r => r.startsWith('location:')).map(r => r.slice('location:'.length));
}

/** Render children only when the permission is held (workspace-wide, at `refs`, or at `scope` resolved by the server). */
export function Can({ perm, refs, scope, anywhere, fallback = null, children }: {
  perm: string | string[]; refs?: readonly string[]; scope?: string | null; anywhere?: boolean; fallback?: ReactNode; children: ReactNode;
}) {
  const mine = usePermissions().data;
  const at = usePermissionsAt(scope ?? null).data;
  const perms = Array.isArray(perm) ? perm : [perm];
  const ok = perms.some(p => scope ? Boolean(at?.has(p)) : anywhere
    ? Boolean(mine && (mine.workspace.includes(p) || mine.scoped.some(s => s.permissions.includes(p))))
    : canWith(mine, p, refs ?? []));
  return <>{ok ? children : fallback}</>;
}

/** Call after any role / grant / membership change so the UI re-evaluates. */
export function invalidatePermissions() {
  return queryClient.invalidateQueries({ queryKey: permKeys.all });
}
